import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRealtimeSubscription } from "@/hooks/use-realtime";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UserPlus, Search } from "lucide-react";
import { ClientsTable } from "@/components/clients/ClientsTable";
import { AddClientDialog } from "@/components/clients/AddClientDialog";
import { EditClientDialog } from "@/components/clients/EditClientDialog";
import { useToast } from "@/components/ui/use-toast";

export type Client = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  company: string | null;
  address: string | null;
  notes: string | null;
  service_types: string[] | null;
  user_id: string;
  source?: 'manual' | 'signup' | 'service_request';
};

const Clients = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const { toast } = useToast();

  // Enable realtime updates
  useRealtimeSubscription("customers", "clients-customers");
  useRealtimeSubscription("profiles", "clients-profiles");
  useRealtimeSubscription("service_requests", "clients-service-requests");

  // Fetch manually added clients
  const { data: manualClients } = useQuery({
    queryKey: ["clients-customers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customers")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data.map(client => ({ ...client, source: 'manual' as const }));
    },
  });

  // Fetch signed up clients
  const { data: signupClients } = useQuery({
    queryKey: ["clients-profiles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_type", "client")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data.map(profile => ({
        id: profile.id,
        name: `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'N/A',
        email: profile.email,
        phone: null,
        company: null,
        address: null,
        notes: 'Client Account',
        service_types: null,
        user_id: profile.id,
        source: 'signup' as const
      }));
    },
  });

  // Fetch service request clients
  const { data: serviceRequestClients } = useQuery({
    queryKey: ["clients-service-requests"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("service_requests")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      
      // Group by email to avoid duplicates
      const uniqueClients = new Map();
      data.forEach(request => {
        if (!uniqueClients.has(request.email)) {
          uniqueClients.set(request.email, {
            id: request.id,
            name: request.contact_name,
            email: request.email,
            phone: request.phone,
            company: request.business_name,
            address: null,
            notes: `Service Request: ${request.service_type} (${request.status})`,
            service_types: [request.service_type],
            user_id: request.client_id,
            source: 'service_request' as const
          });
        }
      });
      
      return Array.from(uniqueClients.values());
    },
  });

  // Merge all client sources and remove duplicates by email
  const allClients = useMemo(() => {
    const clientMap = new Map<string, Client>();
    
    // Add manual clients first (highest priority)
    manualClients?.forEach(client => {
      if (client.email) {
        clientMap.set(client.email.toLowerCase(), client);
      } else {
        clientMap.set(`manual-${client.id}`, client);
      }
    });
    
    // Add signup clients if not already added
    signupClients?.forEach(client => {
      if (client.email && !clientMap.has(client.email.toLowerCase())) {
        clientMap.set(client.email.toLowerCase(), client);
      }
    });
    
    // Add service request clients if not already added
    serviceRequestClients?.forEach(client => {
      if (client.email && !clientMap.has(client.email.toLowerCase())) {
        clientMap.set(client.email.toLowerCase(), client);
      }
    });
    
    return Array.from(clientMap.values());
  }, [manualClients, signupClients, serviceRequestClients]);

  const clients = allClients;

  const filteredClients = clients?.filter(
    (client) =>
      client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.phone?.includes(searchTerm) ||
      client.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddClient = async (client: Omit<Client, "id" | "user_id">) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to add clients",
        variant: "destructive",
      });
      return;
    }

    const { error } = await supabase.from("customers").insert([{
      ...client,
      user_id: user.id
    }]);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to add client",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Success",
      description: "Client added successfully",
    });
    setIsAddDialogOpen(false);
  };

  const handleEditClient = async (client: Client) => {
    const { error } = await supabase
      .from("customers")
      .update({
        name: client.name,
        email: client.email,
        phone: client.phone,
        company: client.company,
        address: client.address,
        notes: client.notes,
        service_types: client.service_types,
      })
      .eq("id", client.id);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to update client",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Success",
      description: "Client updated successfully",
    });
    setSelectedClient(null);
  };

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Clients</h1>
          <p className="text-muted-foreground mt-2">
            All clients: manual entries, signups & service requests ({clients.length} total)
          </p>
        </div>
        <Button onClick={() => setIsAddDialogOpen(true)}>
          <UserPlus className="mr-2 h-4 w-4" />
          Add Client
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search clients..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      <ClientsTable
        clients={filteredClients || []}
        onEdit={setSelectedClient}
      />

      <AddClientDialog
        open={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        onAdd={handleAddClient}
      />

      <EditClientDialog
        client={selectedClient}
        onClose={() => setSelectedClient(null)}
        onSave={handleEditClient}
      />
    </div>
  );
};

export default Clients;